import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./signUp.css";
import { registerUser } from './api'; 

const SignUp = () => {
  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organization, setOrganization] = useState("");
  const [emailId, setEmailId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // ---------- VALIDATORS ----------
  const validateUserName = (value) => (!value.trim() ? "User name is required" : "");
  const validateFirstName = (value) => (!value.trim() ? "First name is required" : "");
  const validateLastName = (value) => (!value.trim() ? "Last name is required" : "");
  const validateOrganization = (value) => (!value.trim() ? "Organization is required" : "");
  const validateEmail = (value) => {
    if (!value.trim()) return "Email ID is required";
    const isEmail = /\S+@\S+\.\S+/.test(value);
    return !isEmail ? "Invalid email ID" : "";
  };
  const validatePhoneNumber = (value) => {
    if (!value.trim()) return "Phone number is required";
    const isPhone = /^\d{10}$/.test(value);
    return !isPhone ? "Invalid phone number" : "";
  };
  const validatePassword = (value) => {
    if (!value.trim()) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters";
    if (!/[A-Z]/.test(value)) return "Password must contain at least one uppercase letter";
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(value))
      return "Password must contain at least one special character";
    return "";
  };
  const validateConfirmPassword = (value, password) => {
    if (!value.trim()) return "Confirm password is required";
    if (value !== password) return "Passwords do not match";
    return "";
  };

  const handleBlur = (field, value) => {
    let newErrors = { ...errors };
    if (field === "userName") newErrors.userName = validateUserName(value);
    if (field === "firstName") newErrors.firstName = validateFirstName(value);
    if (field === "lastName") newErrors.lastName = validateLastName(value);
    if (field === "password") newErrors.password = validatePassword(value);
    if (field === "organization") newErrors.organization = validateOrganization(value);
    if (field === "emailId") newErrors.emailId = validateEmail(value);
    if (field === "phoneNumber") newErrors.phoneNumber = validatePhoneNumber(value);
    if (field === "confirmPassword") newErrors.confirmPassword = validateConfirmPassword(value, password);
    setErrors(newErrors);
  };

  // ---------- SIGN UP ----------
  const handleSignUp = async (e) => {
    e.preventDefault();

    const userNameError = validateUserName(userName);
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const passwordError = validatePassword(password);
    const organizationError = validateOrganization(organization);
    const emailError = validateEmail(emailId);
    const phoneNumberError = validatePhoneNumber(phoneNumber);
    const confirmPasswordError = validateConfirmPassword(confirmPassword, password);

    if (
      userNameError ||
      firstNameError ||
      lastNameError ||
      passwordError ||
      organizationError ||
      emailError ||
      phoneNumberError ||
      confirmPasswordError
    ) {
      setErrors({
        userName: userNameError,
        firstName: firstNameError,
        lastName: lastNameError,
        password: passwordError,
        organization: organizationError,
        emailId: emailError,
        phoneNumber: phoneNumberError,
        confirmPassword: confirmPasswordError,
      });
      return;
    }

    try {
      const response = await registerUser({
        user_name: userName,
        first_name: firstName,
        last_name: lastName,
        organization_name: organization,
        email_id: emailId,
        phone_no: phoneNumber,
        password: password,
      });

      if (response.status === 200 && response.data.success === true) {
        alert(response.data.message ?? "Registration successful.");

        // ✅ Current flow: go to login
        navigate("/login");

        // 🔄 OPTIONAL: Auto-login directly after signup
        /*
        localStorage.setItem("user", JSON.stringify({ email: emailId }));
        localStorage.setItem("isLoggedIn", "true");
        navigate("/HomePage");
        */
      } else {
        alert(response.data.message ?? "Registration failed. Please try again.");
      }
    } catch (error) {
      alert("Something went wrong. Please try again.");
    }
  };

  // ---------- RENDER ----------
  return (
    <div className="signUp-page">
      <div className="signUp-container">
        <img src="/favicon.ico" alt="Logo" className="logo-icon" />
        <h2 className="signUp-header">EnergyAtlas - EMS</h2>

        <form onSubmit={handleSignUp}>
          {/* Username */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.userName ? "error" : ""}`}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onBlur={(e) => handleBlur("userName", e.target.value)}
              placeholder=" "
            />
            <label className={errors.userName ? "error-label" : ""}>User Name *</label>
            {errors.userName && <div className="error-message">{errors.userName}</div>}
          </div>

          {/* First Name */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.firstName ? "error" : ""}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={(e) => handleBlur("firstName", e.target.value)}
              placeholder=" "
            />
            <label className={errors.firstName ? "error-label" : ""}>First Name *</label>
            {errors.firstName && <div className="error-message">{errors.firstName}</div>}
          </div>

          {/* Last Name */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.lastName ? "error" : ""}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={(e) => handleBlur("lastName", e.target.value)}
              placeholder=" "
            />
            <label className={errors.lastName ? "error-label" : ""}>Last Name *</label>
            {errors.lastName && <div className="error-message">{errors.lastName}</div>}
          </div>

          {/* Organization */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.organization ? "error" : ""}`}
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              onBlur={(e) => handleBlur("organization", e.target.value)}
              placeholder=" "
            />
            <label className={errors.organization ? "error-label" : ""}>Organization *</label>
            {errors.organization && <div className="error-message">{errors.organization}</div>}
          </div>

          {/* Email */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.emailId ? "error" : ""}`}
              value={emailId}
              onChange={(e) => setEmailId(e.target.value)}
              onBlur={(e) => handleBlur("emailId", e.target.value)}
              placeholder=" "
            />
            <label className={errors.emailId ? "error-label" : ""}>Email ID *</label>
            {errors.emailId && <div className="error-message">{errors.emailId}</div>}
          </div>

          {/* Phone Number */}
          <div className="input-container">
            <input
              type="text"
              className={`input-box ${errors.phoneNumber ? "error" : ""}`}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onBlur={(e) => handleBlur("phoneNumber", e.target.value)}
              placeholder=" "
            />
            <label className={errors.phoneNumber ? "error-label" : ""}>Phone Number *</label>
            {errors.phoneNumber && <div className="error-message">{errors.phoneNumber}</div>}
          </div>

          {/* Password */}
          <div className="input-container">
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                className={`input-box ${errors.password ? "error" : ""}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={(e) => handleBlur("password", e.target.value)}
                placeholder=" "
              />
              <label className={errors.password ? "error-label" : ""}>Password *</label>
              <span className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>
            {errors.password && <div className="error-message">{errors.password}</div>}
          </div>

          {/* Confirm Password */}
          <div className="input-container">
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                className={`input-box ${errors.confirmPassword ? "error" : ""}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={(e) => handleBlur("confirmPassword", e.target.value)}   
                placeholder=" "
              />
              <label className={errors.confirmPassword ? "error-label" : ""}>
                Confirm Password *
              </label>
            </div>
            {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
          </div>

          <button type="submit" className="login-buttons">SIGN UP</button>
        </form>

        <div className="links">
          <a onClick={() => navigate("/login")}>Login</a>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
